import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { HorizontalScroller } from '../components/HorizontalScroller';
import RolesLegend from '../components/admin/RolesLegend';
import RolePermissionsEditor from '../components/admin/RolePermissionsEditor';
import BaseValuesEditor from '../components/admin/BaseValuesEditor';
import { Users, Shield, Trash2, ChevronDown, Briefcase, UserCheck, UserX, Clock, Search, Upload, Download, Plus, Edit2, X, List } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPanel = () => {
  const { getAuthHeaders, user } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [deniedUsers, setDeniedUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchAdmin, setSearchAdmin] = useState('');
  const [showAliases, setShowAliases] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [newAlias, setNewAlias] = useState({ nome: '', variacoes: '' });
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasSearch, setAliasSearch] = useState('');
  const aliasFileRef = useRef(null);

  const roles = ['dono', 'administrador', 'dublador', 'roteirista', 'mixador', 'visitante'];

  useEffect(() => { fetchUsers(); fetchStats(); fetchPendingUsers(); fetchDeniedUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch { toast.error('Erro ao carregar usuários'); }
    finally { setLoading(false); }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-users`, { headers: getAuthHeaders() });
      setPendingUsers(response.data);
    } catch {}
  };

  const fetchDeniedUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/denied-users`, { headers: getAuthHeaders() });
      setDeniedUsers(response.data);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch {}
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const formData = new FormData();
      formData.append('role', newRole);
      await axios.put(`${API}/admin/users/${userId}/role`, formData, { headers: getAuthHeaders() });
      toast.success('Cargo atualizado!');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao atualizar cargo'); }
  };

  const handleDelete = async (userId, userEmail) => {
    const ok = await confirm(`Tem certeza que deseja excluir o usuário ${userEmail}? Esta ação não pode ser desfeita.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao excluir usuário'); }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Usuário aprovado!');
      fetchPendingUsers();
      fetchDeniedUsers();
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao aprovar'); }
  };

  const handleDeny = async (userId) => {
    const ok = await confirm('Tem certeza que deseja negar o acesso a este usuário?');
    if (!ok) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/deny`, {}, { headers: getAuthHeaders() });
      toast.success('Acesso negado.');
      fetchPendingUsers();
      fetchDeniedUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao negar'); }
  };

  // Voice Aliases CRUD
  const fetchAliases = async () => {
    try {
      const res = await axios.get(`${API}/voice-aliases`, { headers: getAuthHeaders() });
      setAliases(res.data || []);
    } catch (err) { console.error('Failed to fetch aliases:', err); }
  };
  const addAlias = async () => {
    if (!newAlias.nome.trim()) return toast.error('Nome obrigatório');
    try {
      const fd = new FormData(); fd.append('nome', newAlias.nome); fd.append('variacoes', newAlias.variacoes);
      await axios.post(`${API}/voice-aliases`, fd, { headers: getAuthHeaders() });
      setNewAlias({ nome: '', variacoes: '' }); fetchAliases(); toast.success('Adicionado!');
    } catch (err) { toast.error('Erro ao adicionar'); }
  };
  const saveEditAlias = async () => {
    if (!editingAlias) return;
    try {
      const rawVars = editingAlias._rawVars !== undefined ? editingAlias._rawVars : (editingAlias.variacoes || []).join(', ');
      const fd = new FormData(); fd.append('nome', editingAlias.nome); fd.append('variacoes', rawVars);
      await axios.put(`${API}/voice-aliases/${editingAlias.id}`, fd, { headers: getAuthHeaders() });
      setEditingAlias(null); fetchAliases(); toast.success('Atualizado!');
    } catch (err) { toast.error('Erro ao atualizar'); }
  };
  const deleteAlias = async (id) => {
    try {
      await axios.delete(`${API}/voice-aliases/${id}`, { headers: getAuthHeaders() });
      fetchAliases(); toast.success('Removido!');
    } catch (err) { toast.error('Erro ao remover'); }
  };
  const exportAliases = async () => {
    try {
      const res = await axios.get(`${API}/voice-aliases/export`, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.download = 'lista_dubladores.xlsx';
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Lista exportada!');
    } catch (err) { toast.error('Erro ao exportar'); }
  };
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('modo', 'substituir');
      const res = await axios.post(`${API}/voice-aliases/import`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message); fetchAliases();
    } catch (err) { toast.error('Erro ao importar'); }
    if (aliasFileRef.current) aliasFileRef.current.value = '';
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="admin-panel">
        <h1 className="text-3xl md:text-4xl font-bold text-white">Painel Administrativo</h1>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Projetos Ativos" value={stats.active_projects} icon={<Briefcase className="h-6 w-6" />} />
            <StatCard title="Total Usuários" value={stats.total_users} icon={<Users className="h-6 w-6" />} />
            <StatCard title="Aguardando Aprovação" value={pendingUsers.length} icon={<Clock className="h-6 w-6" />} highlight={pendingUsers.length > 0} />
          </div>
        )}

        {/* Pending Login Requests */}
        {pendingUsers.length > 0 && (
          <div className="bg-amber-500/5 rounded-xl border border-amber-500/30 overflow-hidden" data-testid="pending-users-section">
            <div className="p-4 border-b border-amber-500/20 flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-400" />
              <h2 className="text-xl font-semibold text-amber-400">Solicitações de Login ({pendingUsers.length})</h2>
            </div>
            <div className="divide-y divide-slate-700/50">
              {pendingUsers.map(pu => (
                <div key={pu.id} className="px-6 py-4 flex items-center justify-between" data-testid={`pending-user-${pu.id}`}>
                  <div className="flex items-center gap-4">
                    {pu.picture ? (
                      <img src={pu.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold">
                        {pu.name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{pu.name}</p>
                      <p className="text-slate-400 text-sm">{pu.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApprove(pu.id)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors text-sm font-medium"
                      data-testid={`approve-user-${pu.id}`}
                    >
                      <UserCheck className="h-4 w-4" /> Aprovar
                    </button>
                    <button
                      onClick={() => handleDeny(pu.id)}
                      className="flex items-center gap-1.5 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors text-sm font-medium"
                      data-testid={`deny-user-${pu.id}`}
                    >
                      <UserX className="h-4 w-4" /> Negar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Denied Users */}
        {deniedUsers.length > 0 && (
          <div className="bg-red-500/5 rounded-xl border border-red-500/30 overflow-hidden" data-testid="denied-users-section">
            <div className="p-4 border-b border-red-500/20 flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-400" />
              <h2 className="text-xl font-semibold text-red-400">Contas Negadas ({deniedUsers.length})</h2>
            </div>
            <div className="divide-y divide-slate-700/50">
              {deniedUsers.map(du => (
                <div key={du.id} className="px-6 py-4 flex items-center justify-between" data-testid={`denied-user-${du.id}`}>
                  <div className="flex items-center gap-4">
                    {du.picture ? (
                      <img src={du.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold">
                        {du.name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{du.name}</p>
                      <p className="text-slate-400 text-sm">{du.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleApprove(du.id)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors text-sm font-medium"
                    data-testid={`reapprove-user-${du.id}`}
                  >
                    <UserCheck className="h-4 w-4" /> Aprovar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Roles Legend */}
        <RolesLegend />

        {/* Role Permissions Editor (only Dono can edit; others see read-only) */}
        <RolePermissionsEditor />

        {/* Base Values Editor (only Dono can edit; others see read-only) */}
        <BaseValuesEditor />

        {/* Users Table */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-400" />
              Gerenciar Usuários
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchAdmin}
                onChange={e => setSearchAdmin(e.target.value)}
                placeholder="Buscar usuário..."
                className="bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500 w-56"
                data-testid="admin-user-search"
              />
            </div>
          </div>
          <HorizontalScroller layout="block" fadeColor="from-slate-800">
            <table className="w-full min-w-[800px]">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left text-slate-300 px-4 py-3">Nome</th>
                  <th className="text-left text-slate-300 px-4 py-3">Email</th>
                  <th className="text-left text-slate-300 px-4 py-3">Cargo</th>
                  <th className="text-left text-slate-300 px-4 py-3">Disponível</th>
                  <th className="text-left text-slate-300 px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.filter(u => {
                  if (!searchAdmin) return true;
                  const q = searchAdmin.toLowerCase();
                  return (u.name?.toLowerCase().includes(q) || u.artist_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q));
                }).map(u => (
                  <tr key={u.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-medium">
                            {u.name?.charAt(0)}
                          </div>
                        )}
                        <div>
                          <span className="text-white block">{u.artist_name || u.name}</span>
                          {u.artist_name && <span className="text-slate-400 text-xs">{u.name}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.role === 'dono' ? (
                        <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">Dono</span>
                      ) : (
                        <div className="relative">
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1 text-white text-sm focus:outline-none focus:border-teal-500 appearance-none pr-8 cursor-pointer"
                            data-testid={`role-select-${u.id}`}
                          >
                            {roles.filter(r => r !== 'dono').map(role => (
                              <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        u.is_available !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {u.is_available !== false ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role !== 'dono' && (user?.role === 'dono' || user?.role === 'administrador') && u.email !== user?.email && (
                        <button
                          onClick={() => handleDelete(u.id, u.email)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2 rounded-lg transition-colors"
                          data-testid={`delete-user-${u.id}`} title="Excluir usuário"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HorizontalScroller>
        </div>

        {/* Voice Aliases Button */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <button onClick={() => { if (!showAliases) fetchAliases(); setShowAliases(!showAliases); }}
            className="w-full p-4 flex items-center justify-between hover:bg-slate-800 transition-colors" data-testid="toggle-aliases-btn">
            <div className="flex items-center gap-3">
              <List className="h-5 w-5 text-teal-400" />
              <span className="text-white font-semibold text-lg">Lista de Dubladores e Variações</span>
              <span className="text-slate-500 text-sm">({aliases.length})</span>
            </div>
            <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${showAliases ? 'rotate-180' : ''}`} />
          </button>

          {showAliases && (
            <div className="border-t border-slate-700 p-4 space-y-4">
              {/* Actions + Search */}
              <div className="flex flex-wrap gap-3 items-center">
                <button onClick={exportAliases} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm transition-colors" data-testid="export-aliases"><Download className="h-4 w-4" /> Exportar</button>
                <button onClick={() => aliasFileRef.current?.click()} className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg text-sm transition-colors" data-testid="import-aliases"><Upload className="h-4 w-4" /> Importar</button>
                <input type="file" ref={aliasFileRef} onChange={handleImportFile} accept=".xlsx,.xls" className="hidden" />
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input type="text" value={aliasSearch} onChange={e => setAliasSearch(e.target.value)} placeholder="Buscar nome ou variação..."
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-8 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="alias-search" />
                  {aliasSearch && <button onClick={() => setAliasSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
                </div>
              </div>

              {/* Add new */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs text-slate-400 block mb-1">Nome Principal</label>
                  <input type="text" value={newAlias.nome} onChange={e => setNewAlias({...newAlias, nome: e.target.value})}
                    placeholder="Ex: LETTY" autoComplete="off" spellCheck="false" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="alias-nome-input" />
                </div>
                <div className="flex-[2]">
                  <label className="text-xs text-slate-400 block mb-1">Variações (separadas por vírgula)</label>
                  <input type="text" value={newAlias.variacoes} onChange={e => setNewAlias({...newAlias, variacoes: e.target.value})}
                    placeholder="Ex: LETTY DUBS, Lettyvik" autoComplete="off" spellCheck="false" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="alias-variacoes-input"
                    onKeyDown={e => { if (e.key === 'Enter') addAlias(); }} />
                </div>
                <button onClick={addAlias} className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg" data-testid="add-alias-btn"><Plus className="h-4 w-4" /></button>
              </div>

              {/* List */}
              <div className="max-h-[400px] overflow-y-auto space-y-1">
                {(() => {
                  const norm = s => s?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
                  const q = norm(aliasSearch);
                  const filtered = q ? aliases.filter(a => norm(a.nome).includes(q) || (a.variacoes || []).some(v => norm(v).includes(q))) : aliases;
                  if (filtered.length === 0) return <p className="text-slate-500 text-center py-8 text-sm">{aliasSearch ? 'Nenhum resultado encontrado' : 'Nenhum dublador cadastrado. Importe uma lista ou adicione manualmente.'}</p>;
                  return filtered.map((a, i) => (
                  <div key={a.id} className="flex items-center gap-2 bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 group" data-testid={`alias-row-${i}`}>
                    <span className="text-xs text-slate-500 w-6 text-center shrink-0">{i + 1}</span>
                    {editingAlias?.id === a.id ? (
                      <>
                        <input type="text" value={editingAlias.nome} onChange={e => setEditingAlias({...editingAlias, nome: e.target.value})}
                          autoComplete="off" spellCheck="false" className="bg-slate-700 border border-teal-500 rounded px-2 py-1 text-white text-sm flex-1 focus:outline-none" />
                        <input type="text" value={editingAlias._rawVars !== undefined ? editingAlias._rawVars : (editingAlias.variacoes || []).join(', ')}
                          onChange={e => setEditingAlias({...editingAlias, _rawVars: e.target.value})}
                          autoComplete="off" spellCheck="false" className="bg-slate-700 border border-teal-500 rounded px-2 py-1 text-white text-sm flex-[2] focus:outline-none" />
                        <button onClick={saveEditAlias} className="text-teal-400 hover:text-teal-300 p-1"><UserCheck className="h-4 w-4" /></button>
                        <button onClick={() => setEditingAlias(null)} className="text-slate-400 hover:text-white p-1"><X className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <span className="text-white text-sm font-medium flex-1 truncate">{a.nome}</span>
                        <span className="text-slate-400 text-xs flex-[2] truncate">{(a.variacoes || []).join(', ') || '—'}</span>
                        <button onClick={() => setEditingAlias({...a})} className="text-slate-500 hover:text-amber-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Edit2 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteAlias(a.id)} className="text-slate-500 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </div>
                ));
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

const StatCard = ({ title, value, icon, highlight }) => (
  <div className={`rounded-xl p-4 border ${highlight ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-slate-400 text-sm">{title}</p>
        <p className={`text-2xl font-bold mt-1 ${highlight ? 'text-amber-400' : 'text-white'}`}>{value}</p>
      </div>
      <div className={highlight ? 'text-amber-400' : 'text-teal-400'}>{icon}</div>
    </div>
  </div>
);

export default AdminPanel;
